import { useLayerStore } from '@/store/use-layer-store';
import { useWatchfaceScreenShot } from './use-watchface-screenshot';
import { round } from 'lodash-es';
import { AODThemeValidRules } from '@/resources/aod-theme-valid-rules';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { Shape, ShapeType } from '@/services/shape';
import { computed } from 'vue';

export interface AodThemeValidRse {
  themeId: string;
  colorId: string;
  nonBlack?: {
    valid: boolean;
    /** 允许最大值 */
    max: number;
    ratio: number;
  };
  illegalColor?: {
    valid: boolean;
    /** 允许最大值 */
    max: number;
    ratio: number;
  };
  RGB?: {
    [key in 'movable' | 'partiallyMovable' | 'notMovable']?: {
      valid: boolean;
      invalidShapes: {
        shapeId: string;
        total: number;
        invalid: number;
        ratio: number;
        valid: boolean;
      }[];
      desc?: string;
      tooltip?: string;
    };
  };
}

export const useWatchfaceAodPixel = () => {
  const layerStore = useLayerStore();
  const watchfaceStore = useWatchfaceStore();
  const { screenshot } = useWatchfaceScreenShot();

  /**
   * 校验非黑像素占比和max(R,G)/2>=B规则
   * @returns
   */
  const _validBlackAndColorPixels = async (
    aodTheme: StoreThemeItem,
    colorGroup: StoreThemeColorItem
  ) => {
    // 首先截图（ignoreRotation: 忽略指针旋转角度，避免不同数据源导致非黑像素占比不一致）
    const canvasEl = (await screenshot(undefined, {
      themeName: aodTheme.name,
      themeType: 'AOD',
      themeColor: colorGroup.color,
      forceRenderWhenHidden: true,
      ignoreRotation: true,
      returnType: 'canvas'
    })) as HTMLCanvasElement;
    if (!canvasEl) return;

    // 获取数据
    const w = canvasEl.width ?? 0;
    const h = canvasEl.height ?? 0;
    const data = canvasEl?.getContext('2d')?.getImageData(0, 0, w, h).data;
    const pixels = {
      all: 0,
      notBlack: 0,
      illegalColor: 0
    };
    if (!data) return;
    for (let i = 0; i < (data?.length ?? 0); i += 4) {
      pixels.all++;
      const R = data[i];
      const G = data[i + 1];
      const B = data[i + 2];
      const A = data[i + 3];
      // 获取非黑像素点
      const isBlack = (R === 0 && G === 0 && B === 0) || A == 0;
      // 如果是非黑像素
      if (!isBlack) {
        pixels.notBlack++;
      }
      // 颜色不符合max(R,G)/2>=B的像素
      const isLegalColor = Math.max(R, G) / 2 >= B;
      if (!isLegalColor) {
        pixels.illegalColor++;
      }
    }
    // 非黑像素占比
    const notBlackRatio = round(pixels.notBlack / pixels.all, 4);
    // 不合法颜色像素占比
    const illegalColorRatio = round(pixels.illegalColor / pixels.all, 4);

    return { notBlackRatio, illegalColorRatio };
  };

  const _getMovableType = (
    shape: Shape
  ): 'movable' | 'partiallyMovable' | 'notMovable' => {
    const { type } = shape;
    const isTimeSource = [
      'time',
      'timeSecond',
      'timeSecondLow',
      'timeSecondHigh',
      'timeHour',
      'timeHourHigh',
      'timeHourLow',
      'timeHour12H',
      'timeHour24H',
      'timeChineseTime',
      'timeMinute',
      'timeMinuteHigh',
      'timeMinuteLow',
      'dualtime',
      'dualtimeHour',
      'dualtimeHour12H',
      'dualtimeHour24H',
      'dualtimeMinute'
    ].some((key) => shape.soueceKeys.includes(key));

    // 是否可移动
    if (isTimeSource && type == ShapeType.Image) {
      return 'movable';
    }

    // 是否为部分可移动元素
    if (
      isTimeSource &&
      [ShapeType.ImageArray, ShapeType.Text, ShapeType.ProgressBar].includes(
        type
      )
    ) {
      return 'partiallyMovable';
    }

    // 默认返回不可移动元素
    return 'notMovable';
  };

  const getRule = () => {
    const rule = AODThemeValidRules.find((item) =>
      item.deviceTypes.includes(watchfaceStore.deviceType)
    )?.rule;
    return rule;
  };

  const _validShapePixels = async (options: {
    themeName: string;
    themeColor: string;
    shape: Shape;
    rgbValidFn: (R: number, G: number, B: number) => boolean;
    maskAlpha?: number;
  }) => {
    const { themeName, themeColor, shape, rgbValidFn, maskAlpha = 1 } = options;

    // 这里需要针对每个图片元素单独截图，然后进行校验
    // 不能对整体进行截屏，因为整体截屏只会有部分元素
    // 获取截屏
    const canvasEl = (await screenshot(undefined, {
      themeName: themeName,
      themeType: 'AOD',
      themeColor: themeColor,
      shapeIds: [shape.id],
      ignoreRotation: true,
      ignoreBg: true,
      forceRenderWhenHidden: true,
      returnType: 'canvas',
      slotDisplayAll: true,
      useMaxValue: shape.type === 'ProgressBar' ? true : false
    })) as HTMLCanvasElement;
    if (!canvasEl) return;

    // 将文件写入，为了查看具体效果而已
    // if (baseUrl) {
    //   const { path, fs } = ElectronApi;
    //   const temp = await ElectronApi.getPath('temp');
    //   const to = path?.join(
    //     temp,
    //     colorGroup.id + '-' + layer.attrs.name + '.png'
    //   );
    //   if (to)
    //     await fs?.promises.writeFile(
    //       to,
    //       baseUrl.split(',')[1] ?? '',
    //       'base64'
    //     );
    // }

    // 获取数据
    const w = canvasEl.width ?? 0;
    const h = canvasEl.height ?? 0;
    const data = canvasEl?.getContext('2d')?.getImageData(0, 0, w, h).data;
    if (!data) return;

    // 像素点总数
    const pixels = {
      total: 0,
      invalid: 0,
      ratio: 0,
      valid: true
    };
    for (let i = 0; i < (data?.length ?? 0); i += 4) {
      const R = data[i];
      const G = data[i + 1];
      const B = data[i + 2];
      const A = data[i + 3];

      // 忽略纯透明像素点
      // 取消这个规则，过于严格目前影响较大
      // if (A == 0) continue;

      pixels.total++;

      // 获取纯黑像素点
      const isBlack = R === 0 && G === 0 && B === 0;
      // 如果是非黑像素
      if (isBlack) continue;

      // 将透明度应用到 RGB 值上（模拟实际显示效果，叠加蒙层透明度）
      const alpha = (A / 255) * maskAlpha;
      const aR = Math.round(R * alpha);
      const aG = Math.round(G * alpha);
      const aB = Math.round(B * alpha);

      // 判断是否满足规则
      const valid = rgbValidFn(aR, aG, aB);
      if (valid) continue;

      // 下面代表校验失败
      pixels.invalid++;
      // resItem.RGB[movableType].valid = false;
      // console.log(
      //   layer.attrs.name + '，类型是' + movableType,
      //   '像素点->' + i + ' 不满足规则',
      //   desc,
      //   {
      //     R,
      //     G,
      //     B
      //   }
      // );
    }
    if (pixels.invalid) {
      pixels.ratio = round(pixels.invalid / pixels.total, 4);
      pixels.valid = pixels.ratio > 0.05 ? false : true;
      // console.log(
      //   aodTheme.name,
      //   colorGroup.name,
      //   layer.attrs.name,
      //   '类型是' + movableType,
      //   '规则为',
      //   desc,
      //   '不满足规则像素点占比',
      //   round((pixels.invalid / pixels.total) * 100, 2) + '%'
      // );
    }
    return pixels;
  };

  /**
   * 验证RGB颜色
   * @returns
   */
  const _validShapesRGB = async (
    aodTheme: StoreThemeItem,
    colorGroup: StoreThemeColorItem,
    rule: (typeof AODThemeValidRules)[0]['rule']
  ) => {
    const resItem: Partial<AodThemeValidRse['RGB']> = {};

    const stack = [...aodTheme.children];
    while (stack.length) {
      const layer = stack.shift();
      if (!layer) continue;
      if (layer.children?.length) stack.push(...(layer?.children ?? []));
      if ([ShapeType.Widget, ShapeType.Slot].includes(layer.type as ShapeType))
        continue;

      // 如果是指针，需要判断是否为可移动元素

      // 如果是数字图片需要判断是否为部分可移动元素
      // 其他情况都当做不可移动元素
      const shape = Shape.getShape(layer.id);
      if (!shape) continue;

      // 蒙层元素跳过校验
      if (shape.attrs._maskFlag) continue;

      // 判断元素类型
      const movableType = _getMovableType(shape);
      const { valid: _ruleFn, desc, tooltip } = rule['RGB']?.[movableType] ?? {};
      // 如果不满足规则，则不校验
      if (!_ruleFn) continue;

      // 校验shape图片像素点
      const maskLayer = aodTheme.children.find(
        (item) => item.attrs._maskFlag === '_aod_global_mask'
      );
      // 蒙层 attrs.alpha=1（不参与渲染衰减），透光率由图片像素 alpha 决定
      // _maskPixelAlpha 按配色ID存储图片像素 alpha（0~1），透光率 = 1 - 像素alpha
      const maskPixelAlpha = maskLayer?.attrs._maskPixelAlpha?.[colorGroup.id] ?? 0;
      const maskAlpha = 1 - (maskPixelAlpha as number);
      const pixels = await _validShapePixels({
        shape,
        themeName: aodTheme.name,
        themeColor: colorGroup.color,
        rgbValidFn: _ruleFn,
        maskAlpha
      });
      if (!pixels?.valid) console.log('pixels', pixels);

      if (!pixels) continue;

      // shape校验结果
      if (!resItem[movableType]) {
        resItem[movableType] = {
          valid: true,
          invalidShapes: [],
          desc,
          tooltip
        };
      }
      resItem[movableType].valid = pixels.valid
        ? resItem[movableType].valid
        : false;
      resItem[movableType].invalidShapes.push({
        shapeId: shape.id,
        ...pixels
      });
    }

    return resItem;
  };

  const validAODPixels = async () => {
    const details: AodThemeValidRse[] = [];

    // 获取校验规则，如没有则代表不校验
    const rule = getRule();
    if (
      !rule ||
      (!rule.nonBlackRatio && !rule.RGB && rule['max(R,G)/2>=B'] === undefined)
    )
      return;
    // 依次获取所有息屏，所有配色下的息屏图
    // 单一息屏模式下只校验第一个 normal 对应的 AOD
    const isSingle = layerStore.isSingleAOD;
    const aodThemes = isSingle
      ? layerStore.displayThemesForType('AOD')
      : layerStore.themes.filter((item) => item.type == 'AOD');
    if (!aodThemes.length) return;

    for (const aodTheme of aodThemes) {
      // 单一息屏仅校验默认配色，多配色息屏校验全部配色
      const colorGroups =
        isSingle && aodTheme.colorGroupTable?.length
          ? [aodTheme.colorGroupTable[0]]
          : (aodTheme.colorGroupTable ?? []);
      for (const colorGroup of colorGroups) {
        const item: AodThemeValidRse = {
          themeId: aodTheme.id,
          colorId: colorGroup.id
        };

        const { notBlackRatio, illegalColorRatio } =
          (await _validBlackAndColorPixels(aodTheme, colorGroup)) || {};
        // 校验非黑像素面积
        const nonBlackRatioMax = rule.nonBlackRatio;
        if (nonBlackRatioMax) {
          if (notBlackRatio != undefined) {
            item.nonBlack = {
              valid: notBlackRatio <= nonBlackRatioMax,
              max: nonBlackRatioMax,
              ratio: notBlackRatio
            };
          }
        }

        // 校验不合法颜色像素面积，暂定20%一下合格，后续上线后可根据实际情况调整
        if (rule['max(R,G)/2>=B']) {
          if (illegalColorRatio != undefined) {
            item.illegalColor = {
              valid: illegalColorRatio <= 0.2,
              max: 0.2,
              ratio: illegalColorRatio
            };
          }
        }

        // 校验可移动或不可移动的RGB像素
        const rgbRes = await _validShapesRGB(aodTheme, colorGroup, rule);

        if (rgbRes) item.RGB = rgbRes;

        details.push(item);
      }
    }

    return {
      valid: details.some(
        (item) =>
          item.nonBlack?.valid === false ||
          item.illegalColor?.valid === false ||
          item.RGB?.movable?.valid === false ||
          item.RGB?.partiallyMovable?.valid === false ||
          item.RGB?.notMovable?.valid === false
      )
        ? false
        : true,
      details: details
    };
  };

  /**
   * 计算是否可以使用AOD验证
   * @returns {boolean} 如果存在有效的验证规则，则返回true；否则返回false
   */
  const canuseAODValid = computed(() => {
    // 获取验证规则
    const rule = getRule();
    // 检查规则是否存在，并且是否包含RGB规则或非黑像素比例规则
    return Boolean(
      rule &&
      (rule.RGB || rule?.nonBlackRatio || rule['max(R,G)/2>=B'] !== undefined)
    );
  });
  /**
   * 当前设备的息屏校验规则描述（用于 UI 展示）
   */
  const ruleDescArr = computed(() => {
    const rule = getRule();
    const res: { desc: string; tooltip?: string }[] = [];
    if (rule?.nonBlackRatio) {
      res.push({ desc: `非黑像素占比不可超过${rule.nonBlackRatio * 100}%` });
    }
    if (rule?.['max(R,G)/2>=B']) {
      res.push({ desc: `不合法颜色（max(R,G)/2>=B）像素占比不可超过20%` });
    }
    if (rule?.RGB?.movable) {
      res.push({ desc: rule.RGB.movable.desc, tooltip: rule.RGB.movable.tooltip });
    }
    if (rule?.RGB?.partiallyMovable) {
      res.push({ desc: rule.RGB.partiallyMovable.desc, tooltip: rule.RGB.partiallyMovable.tooltip });
    }
    if (rule?.RGB?.notMovable) {
      res.push({ desc: rule.RGB.notMovable.desc, tooltip: rule.RGB.notMovable.tooltip });
    }
    return res;
  });

  return {
    canuseAODValid,
    validAODPixels,
    getRule,
    ruleDescArr
  };
};
